import React, { useState, useEffect, useCallback, useRef } from "react";
import apiClient from "../../../api/apiClient";
import { CITIES_BY_STATE } from "../../AdminPanel/tabs/CRM/shared/usCities";
import "./InboundSegments.css";

// ── Funnel section config ─────────────────────────────────────────────────────
const SECTION_CONFIG = [
    {
        key:             "tofu",
        label:           "TOFU",
        sublabel:        "Top of Funnel",
        status:          "new",
        priority:        "cold",
        color:           "#6E94B4",
        accentColor:     "#3E7DAD",
        flex:            10,
        clipTopRight:    100,
        clipBottomRight: 88,
    },
    {
        key:             "mofu",
        label:           "MOFU",
        sublabel:        "Middle of Funnel",
        status:          "active",
        priority:        "warm",
        color:           "#4E9490",
        accentColor:     "#2D8080",
        flex:            19,
        clipTopRight:    88,
        clipBottomRight: 65,
    },
    {
        key:             "bofu",
        label:           "BOFU",
        sublabel:        "Bottom of Funnel",
        status:          "active",
        priority:        "hot",
        color:           "#459E74",
        accentColor:     "#2E8060",
        flex:            13,
        clipTopRight:    65,
        clipBottomRight: 49,
    },
];

const US_STATES = [
    { abbr: "AL", name: "Alabama" }, { abbr: "AK", name: "Alaska" },
    { abbr: "AZ", name: "Arizona" }, { abbr: "AR", name: "Arkansas" },
    { abbr: "CA", name: "California" }, { abbr: "CO", name: "Colorado" },
    { abbr: "CT", name: "Connecticut" }, { abbr: "DE", name: "Delaware" },
    { abbr: "FL", name: "Florida" }, { abbr: "GA", name: "Georgia" },
    { abbr: "HI", name: "Hawaii" }, { abbr: "ID", name: "Idaho" },
    { abbr: "IL", name: "Illinois" }, { abbr: "IN", name: "Indiana" },
    { abbr: "IA", name: "Iowa" }, { abbr: "KS", name: "Kansas" },
    { abbr: "KY", name: "Kentucky" }, { abbr: "LA", name: "Louisiana" },
    { abbr: "ME", name: "Maine" }, { abbr: "MD", name: "Maryland" },
    { abbr: "MA", name: "Massachusetts" }, { abbr: "MI", name: "Michigan" },
    { abbr: "MN", name: "Minnesota" }, { abbr: "MS", name: "Mississippi" },
    { abbr: "MO", name: "Missouri" }, { abbr: "MT", name: "Montana" },
    { abbr: "NE", name: "Nebraska" }, { abbr: "NV", name: "Nevada" },
    { abbr: "NH", name: "New Hampshire" }, { abbr: "NJ", name: "New Jersey" },
    { abbr: "NM", name: "New Mexico" }, { abbr: "NY", name: "New York" },
    { abbr: "NC", name: "North Carolina" }, { abbr: "ND", name: "North Dakota" },
    { abbr: "OH", name: "Ohio" }, { abbr: "OK", name: "Oklahoma" },
    { abbr: "OR", name: "Oregon" }, { abbr: "PA", name: "Pennsylvania" },
    { abbr: "RI", name: "Rhode Island" }, { abbr: "SC", name: "South Carolina" },
    { abbr: "SD", name: "South Dakota" }, { abbr: "TN", name: "Tennessee" },
    { abbr: "TX", name: "Texas" }, { abbr: "UT", name: "Utah" },
    { abbr: "VT", name: "Vermont" }, { abbr: "VA", name: "Virginia" },
    { abbr: "WA", name: "Washington" }, { abbr: "WV", name: "West Virginia" },
    { abbr: "WI", name: "Wisconsin" }, { abbr: "WY", name: "Wyoming" },
    { abbr: "DC", name: "District of Columbia" },
];

const LEAD_TYPES = [
    { value: "retailer_brand", label: "Retailer / Brand" },
    { value: "grower",         label: "Grower" },
    { value: "consumer",       label: "Consumer" },
];

const STATUSES = [
    { value: "new",       label: "New" },
    { value: "active",    label: "Active" },
    { value: "won",       label: "Won" },
    { value: "lost",      label: "Lost" },
    { value: "converted", label: "Converted" },
];

const PRIORITIES = [
    { value: "cold", label: "Cold" },
    { value: "warm", label: "Warm" },
    { value: "hot",  label: "Hot" },
];

const EMPTY_FORM = { name: "", description: "", state: "", city: "", leadType: "", status: "", priority: "" };

const ADD_LEADS_LIMIT = 25;

// ── Component ─────────────────────────────────────────────────────────────────
const InboundSegments = ({ onCreateCampaign, onSelectLead }) => {
    const [counts, setCounts]                   = useState({ tofu: 0, mofu: 0, bofu: 0 });
    const [activeSection, setActiveSection]     = useState(null);
    const [segments, setSegments]               = useState([]);
    const [loadingSegs, setLoadingSegs]         = useState(false);
    const [showCreate, setShowCreate]           = useState(false);
    const [form, setForm]                       = useState(EMPTY_FORM);
    const [creating, setCreating]               = useState(false);
    const [createError, setCreateError]         = useState("");
    const [selectedSegment, setSelectedSegment] = useState(null);
    const [segLeads, setSegLeads]               = useState([]);
    const [loadingSegLeads, setLoadingSegLeads] = useState(false);

    // ── Connect Automation modal state ────────────────────────────────────────
    const [automations, setAutomations]             = useState([]);
    const [automationsLoaded, setAutomationsLoaded] = useState(false);
    const [showConnectModal, setShowConnectModal]   = useState(false);
    const [connectTarget, setConnectTarget]         = useState(null);
    const [connectAutoId, setConnectAutoId]         = useState(null);
    const [connectSaving, setConnectSaving]         = useState(false);

    // ── Add Leads modal state ──────────────────────────────────────────────────
    const [showAddLeads, setShowAddLeads]           = useState(false);
    const [alFilters, setAlFilters]                 = useState({ search: "", leadType: "", state: "", city: "" });
    const [alPage, setAlPage]                       = useState(1);
    const [alLeads, setAlLeads]                     = useState([]);
    const [alTotal, setAlTotal]                     = useState(0);
    const [alLoading, setAlLoading]                 = useState(false);
    const [alSelected, setAlSelected]               = useState(new Set());
    const [alSaving, setAlSaving]                   = useState(false);
    const alAvailableCities = alFilters.state ? (CITIES_BY_STATE[alFilters.state] || []) : [];

    // ── Folder / drag state ───────────────────────────────────────────────────
    // displayItems: flat ordered list of what to render — either a standalone segment or a folder
    // folders: { [folderId]: { name, segmentIds, collapsed, editingName } }
    const [displayItems, setDisplayItems]   = useState([]);
    const [folders, setFolders]             = useState({});
    const [folderCounter, setFolderCounter] = useState(1);
    const [draggingSegId, setDraggingSegId] = useState(null);
    const [dragOverKey, setDragOverKey]     = useState(null);
    // Ref so drag drop handlers can read latest folders without stale closures
    const foldersRef = useRef({});
    useEffect(() => { foldersRef.current = folders; }, [folders]);

    // Persist folder layout for the active section to localStorage on every change
    useEffect(() => {
        if (!activeSection) return;
        try {
            localStorage.setItem(`crm_seg_folders_${activeSection.key}`, JSON.stringify({
                displayItems,
                folders,
                folderCounter,
            }));
        } catch {}
    }, [displayItems, folders, folderCounter, activeSection?.key]);

    // Smart-merge displayItems whenever the segments array updates.
    // Preserves existing folder structure; adds new segs at the end, removes deleted ones.
    useEffect(() => {
        const segIds = new Set(segments.map(s => s.id));
        const currentFolders = foldersRef.current;
        const allInFolders = new Set(Object.values(currentFolders).flatMap(f => f.segmentIds));

        // Clean up folders — remove any deleted segment IDs
        setFolders(prev => {
            const updated = {};
            for (const [id, folder] of Object.entries(prev)) {
                updated[id] = { ...folder, segmentIds: folder.segmentIds.filter(sid => segIds.has(sid)) };
            }
            return updated;
        });

        setDisplayItems(prev => {
            // Remove display entries for deleted segments
            const filtered = prev.filter(item => item.type === "folder" || segIds.has(item.segId));
            // Track which segs are already represented somewhere
            const trackedInItems = new Set(filtered.filter(i => i.type === "segment").map(i => i.segId));
            // Append genuinely new segments (not in displayItems and not in any folder)
            const newSegs = segments.filter(s => !trackedInItems.has(s.id) && !allInFolders.has(s.id));
            return [...filtered, ...newSegs.map(s => ({ type: "segment", segId: s.id }))];
        });
    }, [segments]);

    useEffect(() => {
        apiClient.get("/admin/crm/inbound/funnel-stats")
            .then(res => setCounts(res.data))
            .catch(() => {});
    }, []);

    const fetchSegments = useCallback(async (key) => {
        setLoadingSegs(true);
        try {
            const res = await apiClient.get(`/admin/crm/inbound/segments?funnelSection=${key}`);
            setSegments(res.data.segments || []);
        } catch {
            setSegments([]);
        } finally {
            setLoadingSegs(false);
        }
    }, []);

    const handleSectionClick = (sec) => {
        setActiveSection(sec);
        setShowCreate(false);
        setCreateError("");
        setForm(EMPTY_FORM);
        setSelectedSegment(null);
        setSegLeads([]);
        setDraggingSegId(null);
        setDragOverKey(null);
        // Restore persisted folder layout for this section, or start fresh
        try {
            const raw = localStorage.getItem(`crm_seg_folders_${sec.key}`);
            if (raw) {
                const { displayItems: d, folders: f, folderCounter: c } = JSON.parse(raw);
                setDisplayItems(d || []);
                setFolders(f || {});
                setFolderCounter(c || 1);
            } else {
                setDisplayItems([]);
                setFolders({});
                setFolderCounter(1);
            }
        } catch {
            setDisplayItems([]);
            setFolders({});
            setFolderCounter(1);
        }
        fetchSegments(sec.key);
    };

    const handleCreate = async () => {
        if (!form.name.trim()) { setCreateError("Segment name is required."); return; }
        setCreating(true);
        setCreateError("");
        try {
            await apiClient.post("/admin/crm/inbound/segments", {
                funnelSection: activeSection.key,
                name:          form.name.trim(),
                description:   form.description.trim() || null,
                filters: {
                    status:   form.status   || null,
                    priority: form.priority || null,
                    state:    form.state    || null,
                    city:     form.city     || null,
                    leadType: form.leadType || null,
                },
            });
            setShowCreate(false);
            setForm(EMPTY_FORM);
            fetchSegments(activeSection.key);
        } catch (err) {
            setCreateError(err.response?.data?.error || "Failed to create segment");
        } finally {
            setCreating(false);
        }
    };

    const handleDelete = async (e, segId) => {
        e.stopPropagation();
        if (!window.confirm("Delete this segment?")) return;
        try {
            await apiClient.delete(`/admin/crm/inbound/segments/${segId}`);
            fetchSegments(activeSection.key);
        } catch (err) {
            alert(err.response?.data?.error || "Failed to delete segment");
        }
    };

    const handleSegmentClick = (seg) => {
        setSelectedSegment(seg);
        fetchSegLeads(seg);
    };

    const handleBackFromSegment = () => {
        setSelectedSegment(null);
        setSegLeads([]);
    };

    const fetchSegLeads = useCallback(async (seg) => {
        setLoadingSegLeads(true);
        try {
            const res = await apiClient.get(`/admin/crm/inbound/segments/${seg.id}/leads`);
            setSegLeads(res.data.leads || []);
        } catch {
            setSegLeads([]);
        } finally {
            setLoadingSegLeads(false);
        }
    }, []);

    // ── Add Leads modal helpers ────────────────────────────────────────────────
    const fetchAlLeads = useCallback(async (filters, page) => {
        setAlLoading(true);
        try {
            const params = new URLSearchParams({ page, limit: ADD_LEADS_LIMIT, sort: "createdAt", order: "desc" });
            if (filters.search)   { params.set("firstName", filters.search); }
            if (filters.leadType) params.set("leadType", filters.leadType);
            if (filters.state)    params.set("state", filters.state);
            if (filters.city)     params.set("city", filters.city);
            const res = await apiClient.get(`/admin/crm/inbound/leads?${params}`);
            setAlLeads(res.data.leads || []);
            setAlTotal(res.data.total || 0);
        } catch {
            setAlLeads([]);
            setAlTotal(0);
        } finally {
            setAlLoading(false);
        }
    }, []);

    const openAddLeads = () => {
        setAlFilters({ search: "", leadType: "", state: "", city: "" });
        setAlPage(1);
        setAlSelected(new Set());
        setAlLeads([]);
        setAlTotal(0);
        setShowAddLeads(true);
        fetchAlLeads({ search: "", leadType: "", state: "", city: "" }, 1);
    };

    const handleAlFilterChange = (updated) => {
        const next = { ...alFilters, ...updated };
        if (updated.state !== undefined) next.city = "";
        setAlFilters(next);
        setAlPage(1);
        setAlSelected(new Set());
        fetchAlLeads(next, 1);
    };

    const handleAlPageChange = (p) => {
        setAlPage(p);
        fetchAlLeads(alFilters, p);
    };

    const handleAlToggleRow = (id) => {
        setAlSelected(prev => {
            const n = new Set(prev);
            n.has(id) ? n.delete(id) : n.add(id);
            return n;
        });
    };

    const handleAlToggleAll = () => {
        const pageIds = alLeads.map(l => l.id);
        const allSelected = pageIds.every(id => alSelected.has(id));
        setAlSelected(prev => {
            const n = new Set(prev);
            if (allSelected) pageIds.forEach(id => n.delete(id));
            else             pageIds.forEach(id => n.add(id));
            return n;
        });
    };

    const handleAddLeadsConfirm = async () => {
        if (alSelected.size === 0 || !selectedSegment) return;
        setAlSaving(true);
        try {
            await apiClient.post(`/admin/crm/inbound/segments/${selectedSegment.id}/manual-leads`, {
                leadIds: [...alSelected],
            });
            setShowAddLeads(false);
            fetchSegLeads(selectedSegment);
        } catch (err) {
            alert(err.response?.data?.error || "Failed to add leads");
        } finally {
            setAlSaving(false);
        }
    };

    const handleRemoveLead = async (leadId) => {
        if (!selectedSegment) return;
        try {
            await apiClient.delete(`/admin/crm/inbound/segments/${selectedSegment.id}/leads/${leadId}`);
            fetchSegLeads(selectedSegment);
        } catch (err) {
            alert(err.response?.data?.error || "Failed to remove lead");
        }
    };

    const openConnectModal = async (e, seg) => {
        e.stopPropagation();
        setConnectTarget(seg);
        setConnectAutoId(seg.automationId || null);
        setShowConnectModal(true);
        if (!automationsLoaded) {
            try {
                const res = await apiClient.get("/admin/crm/automations");
                setAutomations(res.data.automations || []);
            } catch {
                setAutomations([]);
            } finally {
                setAutomationsLoaded(true);
            }
        }
    };

    const handleConnectConfirm = async () => {
        if (!connectTarget) return;
        setConnectSaving(true);
        try {
            await apiClient.patch(`/admin/crm/inbound/segments/${connectTarget.id}/automation`, {
                automationId: connectAutoId,
            });
            const chosenName = connectAutoId
                ? (automations.find(a => a.id === connectAutoId)?.name || null)
                : null;
            setSegments(prev => prev.map(s =>
                s.id === connectTarget.id
                    ? { ...s, automationId: connectAutoId, automationName: chosenName }
                    : s
            ));
            setShowConnectModal(false);
        } catch (err) {
            alert(err.response?.data?.error || "Failed to update automation");
        } finally {
            setConnectSaving(false);
        }
    };

    // ── Drag & drop handlers ──────────────────────────────────────────────────
    const handleSegDragStart = (e, segId) => {
        setDraggingSegId(segId);
        e.dataTransfer.effectAllowed = "move";
    };

    const handleSegDragEnd = () => {
        setDraggingSegId(null);
        setDragOverKey(null);
    };

    const handleDragOver = (e, key) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        setDragOverKey(key);
    };

    const handleDragLeave = (e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setDragOverKey(null);
        }
    };

    // Drop onto another segment row — creates a new folder or merges into an existing one
    const handleDropOnSegment = (e, targetSegId) => {
        e.preventDefault();
        e.stopPropagation();
        if (!draggingSegId || draggingSegId === targetSegId) { setDragOverKey(null); return; }

        const currentFolders = foldersRef.current;
        const dragInFolder   = Object.entries(currentFolders).find(([, f]) => f.segmentIds.includes(draggingSegId))?.[0] || null;
        const targetInFolder = Object.entries(currentFolders).find(([, f]) => f.segmentIds.includes(targetSegId))?.[0] || null;

        if (targetInFolder) {
            // Target already lives in a folder — move the dragged seg into that same folder
            if (dragInFolder === targetInFolder) { setDragOverKey(null); return; }
            setFolders(prev => {
                const updated = { ...prev };
                if (dragInFolder && updated[dragInFolder]) {
                    updated[dragInFolder] = { ...updated[dragInFolder], segmentIds: updated[dragInFolder].segmentIds.filter(id => id !== draggingSegId) };
                }
                updated[targetInFolder] = { ...updated[targetInFolder], segmentIds: [...updated[targetInFolder].segmentIds, draggingSegId] };
                return updated;
            });
            if (!dragInFolder) {
                setDisplayItems(prev => prev.filter(item => !(item.type === "segment" && item.segId === draggingSegId)));
            }
        } else {
            // Neither seg is in a folder — create a new one at the target's position
            const newFolderId = `folder-${Date.now()}`;
            const folderName  = `Folder ${folderCounter}`;
            setFolderCounter(c => c + 1);

            const dragSegId = draggingSegId; // capture before async state update
            setFolders(prev => {
                const updated = {
                    ...prev,
                    [newFolderId]: { name: folderName, segmentIds: [targetSegId, dragSegId], collapsed: false, editingName: false },
                };
                if (dragInFolder && updated[dragInFolder]) {
                    updated[dragInFolder] = { ...updated[dragInFolder], segmentIds: updated[dragInFolder].segmentIds.filter(id => id !== dragSegId) };
                }
                return updated;
            });

            setDisplayItems(prev => {
                const targetIdx = prev.findIndex(item => item.type === "segment" && item.segId === targetSegId);
                const filtered  = prev.filter(item => !(item.type === "segment" && (item.segId === dragSegId || item.segId === targetSegId)));
                const insertAt  = targetIdx === -1 ? filtered.length : Math.min(targetIdx, filtered.length);
                return [...filtered.slice(0, insertAt), { type: "folder", folderId: newFolderId }, ...filtered.slice(insertAt)];
            });
        }

        setDraggingSegId(null);
        setDragOverKey(null);
    };

    // Drop onto a folder header — add the dragged segment into that folder
    const handleDropOnFolder = (e, folderId) => {
        e.preventDefault();
        e.stopPropagation();
        if (!draggingSegId) { setDragOverKey(null); return; }

        const currentFolders = foldersRef.current;
        const dragInFolder   = Object.entries(currentFolders).find(([, f]) => f.segmentIds.includes(draggingSegId))?.[0] || null;
        if (dragInFolder === folderId) { setDragOverKey(null); return; }

        const dragSegId = draggingSegId;
        setFolders(prev => {
            const updated = { ...prev };
            if (dragInFolder && updated[dragInFolder]) {
                updated[dragInFolder] = { ...updated[dragInFolder], segmentIds: updated[dragInFolder].segmentIds.filter(id => id !== dragSegId) };
            }
            if (updated[folderId]) {
                updated[folderId] = { ...updated[folderId], segmentIds: [...updated[folderId].segmentIds, dragSegId] };
            }
            return updated;
        });

        if (!dragInFolder) {
            setDisplayItems(prev => prev.filter(item => !(item.type === "segment" && item.segId === dragSegId)));
        }

        setDraggingSegId(null);
        setDragOverKey(null);
    };

    // ── Folder management helpers ─────────────────────────────────────────────
    const handleRemoveFromFolder = (segId, folderId) => {
        setFolders(prev => ({
            ...prev,
            [folderId]: { ...prev[folderId], segmentIds: prev[folderId].segmentIds.filter(id => id !== segId) },
        }));
        setDisplayItems(prev => [...prev, { type: "segment", segId }]);
    };

    const handleDeleteFolder = (folderId) => {
        const segIds = foldersRef.current[folderId]?.segmentIds || [];
        setFolders(prev => { const u = { ...prev }; delete u[folderId]; return u; });
        setDisplayItems(prev => {
            const idx      = prev.findIndex(item => item.type === "folder" && item.folderId === folderId);
            const filtered = prev.filter(item => !(item.type === "folder" && item.folderId === folderId));
            const newItems = segIds.map(id => ({ type: "segment", segId: id }));
            const insertAt = idx === -1 ? filtered.length : Math.min(idx, filtered.length);
            return [...filtered.slice(0, insertAt), ...newItems, ...filtered.slice(insertAt)];
        });
    };

    const startFolderRename = (folderId) => {
        setFolders(prev => ({ ...prev, [folderId]: { ...prev[folderId], editingName: true } }));
    };

    const handleFolderRenameBlur = (folderId, newName) => {
        setFolders(prev => ({
            ...prev,
            [folderId]: { ...prev[folderId], name: newName.trim() || prev[folderId].name, editingName: false },
        }));
    };

    const toggleFolderCollapse = (folderId) => {
        setFolders(prev => ({ ...prev, [folderId]: { ...prev[folderId], collapsed: !prev[folderId].collapsed } }));
    };

    const formCities = form.state ? (CITIES_BY_STATE[form.state] || []) : [];

    const leadTypeLabel = (val) => LEAD_TYPES.find(t => t.value === val)?.label || val;
    const stateLabel    = (abbr) => US_STATES.find(s => s.abbr === abbr)?.name || abbr;

    // ── Render helpers ────────────────────────────────────────────────────────
    const renderSegmentRow = (seg, inFolderId = null) => (
        <div
            key={seg.id}
            className={[
                "iseg-row",
                draggingSegId === seg.id      ? "iseg-row--dragging"  : "",
                dragOverKey === `seg-${seg.id}` ? "iseg-row--drag-over" : "",
            ].filter(Boolean).join(" ")}
            draggable
            onDragStart={e => handleSegDragStart(e, seg.id)}
            onDragEnd={handleSegDragEnd}
            onDragOver={e => handleDragOver(e, `seg-${seg.id}`)}
            onDragLeave={handleDragLeave}
            onDrop={e => handleDropOnSegment(e, seg.id)}
            onClick={() => handleSegmentClick(seg)}
        >
            {/* Grip handle — stops click propagation so dragging doesn't open the segment */}
            <span
                className="iseg-row__drag-handle"
                onClick={e => e.stopPropagation()}
            >
                <svg width="10" height="14" viewBox="0 0 10 16" fill="currentColor" aria-hidden="true">
                    <circle cx="3" cy="3"  r="1.3"/>
                    <circle cx="7" cy="3"  r="1.3"/>
                    <circle cx="3" cy="8"  r="1.3"/>
                    <circle cx="7" cy="8"  r="1.3"/>
                    <circle cx="3" cy="13" r="1.3"/>
                    <circle cx="7" cy="13" r="1.3"/>
                </svg>
            </span>
            <span
                className="iseg-row__dot"
                style={{ background: activeSection.accentColor }}
            />
            <div className="iseg-row__info">
                <span className="iseg-row__name">{seg.name}</span>
                {seg.description && (
                    <span className="iseg-row__desc">{seg.description}</span>
                )}
                <div className="iseg-row__chips">
                    {seg.filters?.state && (
                        <span className="iseg-chip">{stateLabel(seg.filters.state)}</span>
                    )}
                    {seg.filters?.city && (
                        <span className="iseg-chip">{seg.filters.city}</span>
                    )}
                    {seg.filters?.leadType && (
                        <span className="iseg-chip">{leadTypeLabel(seg.filters.leadType)}</span>
                    )}
                    <span className="iseg-chip iseg-chip--count">
                        {seg.leadCount} lead{seg.leadCount !== 1 ? "s" : ""}
                    </span>
                    {seg.automationName && (
                        <span className="iseg-chip iseg-chip--automation">
                            ⚡ {seg.automationName}
                        </span>
                    )}
                </div>
            </div>
            <div className="iseg-row__actions">
                {inFolderId && (
                    <button
                        className="iseg-row__unfolder-btn"
                        title="Remove from folder"
                        onClick={e => { e.stopPropagation(); handleRemoveFromFolder(seg.id, inFolderId); }}
                    >
                        {/* Eject / move-out icon */}
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <polyline points="17 8 12 3 7 8"/>
                            <line x1="12" y1="3" x2="12" y2="15"/>
                            <path d="M3 17v2a2 2 0 002 2h14a2 2 0 002-2v-2"/>
                        </svg>
                    </button>
                )}
                <button
                    className="admin-btn admin-btn--secondary iseg-row__connect-btn"
                    onClick={(e) => openConnectModal(e, seg)}
                >
                    {seg.automationId ? "Change Flow" : "Connect Automation"}
                </button>
                <button
                    className="admin-btn admin-btn--danger"
                    onClick={(e) => handleDelete(e, seg.id)}
                >
                    Delete
                </button>
            </div>
        </div>
    );

    const renderFolder = (folderId) => {
        const folder = folders[folderId];
        if (!folder) return null;
        const folderSegs  = folder.segmentIds.map(id => segments.find(s => s.id === id)).filter(Boolean);
        const isDragOver  = dragOverKey === `folder-${folderId}`;

        return (
            <div
                key={folderId}
                className={`iseg-folder${isDragOver ? " iseg-folder--drag-over" : ""}`}
                onDragOver={e => { if (draggingSegId) handleDragOver(e, `folder-${folderId}`); }}
                onDragLeave={handleDragLeave}
                onDrop={e => handleDropOnFolder(e, folderId)}
            >
                <div className="iseg-folder__header" onClick={() => toggleFolderCollapse(folderId)}>
                    {/* Collapse chevron */}
                    <svg
                        className={`iseg-folder__chevron${folder.collapsed ? "" : " iseg-folder__chevron--open"}`}
                        width="14" height="14" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                        aria-hidden="true"
                    >
                        <polyline points="9 18 15 12 9 6" />
                    </svg>
                    {/* Folder icon */}
                    <svg className="iseg-folder__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/>
                    </svg>
                    {folder.editingName ? (
                        <input
                            className="iseg-folder__name-input"
                            value={folder.name}
                            autoFocus
                            onClick={e => e.stopPropagation()}
                            onChange={e => setFolders(prev => ({ ...prev, [folderId]: { ...prev[folderId], name: e.target.value } }))}
                            onBlur={e => handleFolderRenameBlur(folderId, e.target.value)}
                            onKeyDown={e => { if (e.key === "Enter") e.target.blur(); e.stopPropagation(); }}
                        />
                    ) : (
                        <span
                            className="iseg-folder__name"
                            title="Double-click to rename"
                            onDoubleClick={e => { e.stopPropagation(); startFolderRename(folderId); }}
                        >
                            {folder.name}
                        </span>
                    )}
                    <span className="iseg-folder__count">{folderSegs.length}</span>
                    <button
                        className="iseg-folder__delete-btn"
                        title="Dissolve folder — segments return to list"
                        onClick={e => { e.stopPropagation(); handleDeleteFolder(folderId); }}
                    >
                        ×
                    </button>
                </div>
                {!folder.collapsed && (
                    <div className="iseg-folder__body">
                        {folderSegs.length === 0 ? (
                            <p className="iseg-folder__empty">Drop segments here</p>
                        ) : (
                            folderSegs.map(seg => renderSegmentRow(seg, folderId))
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="inbound-segments">
            <div className="iseg-layout">

                {/* ── LEFT: pyramid (30%) ──────────────────────────────────── */}
                <div className="iseg-pyramid">
                    <div className="iseg-pyramid-header">
                        <span className="iseg-pyramid-header__label">Funnel Sections</span>
                    </div>
                    <div className="iseg-pyramid-sections">
                        {SECTION_CONFIG.map(sec => (
                            <div
                                key={sec.key}
                                className={`iseg-sec${activeSection?.key === sec.key ? " iseg-sec--active" : ""}`}
                                style={{
                                    flex:       sec.flex,
                                    background: sec.color,
                                    clipPath:   `polygon(0% 0%, ${sec.clipTopRight}% 0%, ${sec.clipBottomRight}% 100%, 0% 100%)`,
                                }}
                                onClick={() => handleSectionClick(sec)}
                            >
                                <div className="iseg-sec__inner">
                                    <span className="iseg-sec__label">{sec.label}</span>
                                    <span className="iseg-sec__sub">{sec.sublabel}</span>
                                    <span className="iseg-sec__count">
                                        {counts[sec.key]} lead{counts[sec.key] !== 1 ? "s" : ""}
                                    </span>
                                </div>
                            </div>
                        ))}
                        {!activeSection && (
                            <p className="iseg-hint">Click a section to manage its segments</p>
                        )}
                    </div>
                </div>

                {/* ── RIGHT: panel (70%) ──────────────────────────────────── */}
                <div className={`iseg-panel${!activeSection ? " iseg-panel--empty" : ""}`}>
                    {!activeSection ? (
                        <p className="iseg-panel__placeholder">
                            Select a funnel section on the left<br />to view and manage its segments.
                        </p>
                    ) : (
                        <div className="iseg-panel__scroll">

                            {/* ── Segment detail view ─────────────────────── */}
                            {selectedSegment ? (
                                <div className="iseg-detail">
                                    <button className="iseg-detail__back" onClick={handleBackFromSegment}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="15 18 9 12 15 6" />
                                        </svg>
                                        Back to segments
                                    </button>
                                    <div className="iseg-detail__header">
                                        <div className="iseg-detail__header-row">
                                            <h3 className="iseg-detail__name">{selectedSegment.name}</h3>
                                            <button className="admin-btn admin-btn--primary iseg-detail__add-btn" onClick={openAddLeads}>
                                                + Add Leads
                                            </button>
                                        </div>
                                        {selectedSegment.description && (
                                            <p className="iseg-detail__desc">{selectedSegment.description}</p>
                                        )}
                                        <div className="iseg-row__chips" style={{ marginTop: "var(--space-1)" }}>
                                            {selectedSegment.filters?.status && (
                                                <span className={`crm-status-badge crm-status-badge--${selectedSegment.filters.status}`}>{selectedSegment.filters.status}</span>
                                            )}
                                            {selectedSegment.filters?.priority && (
                                                <span className={`crm-priority-badge crm-priority-badge--${selectedSegment.filters.priority}`}>{selectedSegment.filters.priority}</span>
                                            )}
                                            {selectedSegment.filters?.state && (
                                                <span className="iseg-chip">{stateLabel(selectedSegment.filters.state)}</span>
                                            )}
                                            {selectedSegment.filters?.city && (
                                                <span className="iseg-chip">{selectedSegment.filters.city}</span>
                                            )}
                                            {selectedSegment.filters?.leadType && (
                                                <span className="iseg-chip">{leadTypeLabel(selectedSegment.filters.leadType)}</span>
                                            )}
                                            <span className="iseg-chip iseg-chip--count">
                                                {segLeads.length} lead{segLeads.length !== 1 ? "s" : ""}
                                            </span>
                                        </div>
                                    </div>

                                    <div>
                                        <p className="iseg-detail__leads-title">Leads in this segment</p>
                                        {loadingSegLeads ? (
                                            <p className="iseg-detail__empty">Loading…</p>
                                        ) : segLeads.length === 0 ? (
                                            <p className="iseg-detail__empty">No leads match this segment's filters.</p>
                                        ) : (
                                            <div className="iseg-detail__lead-list">
                                                {segLeads.map(lead => (
                                                    <div key={lead.id} className="iseg-detail__lead-row">
                                                        <div
                                                            className="iseg-detail__lead-clickable"
                                                            onClick={() => onSelectLead && onSelectLead(lead.id)}
                                                        >
                                                            <div className="iseg-detail__lead-name">
                                                                {lead.firstName} {lead.lastName}
                                                                {lead.isManual && (
                                                                    <span className="iseg-manual-badge">Manual</span>
                                                                )}
                                                            </div>
                                                            <div className="iseg-detail__lead-meta">
                                                                {lead.email}
                                                                {lead.leadType && ` · ${leadTypeLabel(lead.leadType)}`}
                                                                {lead.city && lead.state && ` · ${lead.city}, ${lead.state}`}
                                                            </div>
                                                        </div>
                                                        <div className="iseg-detail__lead-actions">
                                                            <button
                                                                className="iseg-detail__remove-btn"
                                                                title="Remove from segment"
                                                                onClick={() => handleRemoveLead(lead.id)}
                                                            >
                                                                ×
                                                            </button>
                                                            <svg className="iseg-detail__lead-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" onClick={() => onSelectLead && onSelectLead(lead.id)} style={{ cursor: "pointer" }}>
                                                                <polyline points="9 18 15 12 9 6" />
                                                            </svg>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                            <React.Fragment>

                            {/* Section title */}
                            <div className="iseg-panel__header">
                                <h2
                                    className="iseg-panel__title"
                                    style={{ color: activeSection.accentColor }}
                                >
                                    {activeSection.label}
                                </h2>
                                <p className="iseg-panel__subtitle">{activeSection.sublabel}</p>
                                <div className="iseg-panel__pills">
                                    <div className="iseg-panel__pill-group">
                                        <span className="iseg-panel__pill-key">Status</span>
                                        <span className={`crm-status-badge crm-status-badge--${activeSection.status}`}>
                                            {activeSection.status}
                                        </span>
                                    </div>
                                    <div className="iseg-panel__pill-group">
                                        <span className="iseg-panel__pill-key">Priority</span>
                                        <span className={`crm-priority-badge crm-priority-badge--${activeSection.priority}`}>
                                            {activeSection.priority}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Segments list */}
                            <div className="iseg-segments-block">
                                <div className="iseg-segments-bar">
                                    <span className="iseg-segments-bar__title">Segments</span>
                                    <button
                                        className="admin-btn admin-btn--primary"
                                        onClick={() => {
                                            setForm({ ...EMPTY_FORM, status: activeSection.status, priority: activeSection.priority });
                                            setShowCreate(true);
                                            setCreateError("");
                                        }}
                                    >
                                        + Create Segment
                                    </button>
                                </div>
                                <div className="iseg-divider" />

                                <div className="iseg-list">
                                    {loadingSegs ? (
                                        <p className="iseg-empty">Loading…</p>
                                    ) : segments.length === 0 ? (
                                        <p className="iseg-empty">
                                            No segments yet for this funnel section.<br />
                                            Create one to define a targeted audience for campaigns.
                                        </p>
                                    ) : (
                                        <>
                                            {displayItems.map(item => {
                                                if (item.type === "segment") {
                                                    const seg = segments.find(s => s.id === item.segId);
                                                    return seg ? renderSegmentRow(seg, null) : null;
                                                }
                                                return renderFolder(item.folderId);
                                            })}
                                            {segments.length > 1 && (
                                                <p className="iseg-drag-hint">
                                                    Drag a segment onto another to group them into a folder
                                                </p>
                                            )}
                                        </>
                                    )}
                                </div>

                                <div className="iseg-divider iseg-divider--bottom" />
                            </div>

                            </React.Fragment>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* ── Connect Automation Modal ─────────────────────────────────── */}
            {showConnectModal && connectTarget && (
                <div
                    className="crm-modal-overlay"
                    onClick={e => e.target === e.currentTarget && setShowConnectModal(false)}
                >
                    <div className="crm-modal iseg-connect-modal">
                        <div className="crm-modal__header">
                            <h2 className="crm-modal__title">Connect Automation — "{connectTarget.name}"</h2>
                            <button className="crm-modal__close" onClick={() => setShowConnectModal(false)} type="button">×</button>
                        </div>
                        <div className="crm-modal__body">
                            <p className="iseg-connect-hint">
                                Select an automation flow to assign to this segment. When triggered, it will run for leads in this segment.
                            </p>
                            <div className="iseg-connect-list">
                                <label className="iseg-connect-item">
                                    <input
                                        type="radio"
                                        name="automation"
                                        value=""
                                        checked={connectAutoId === null}
                                        onChange={() => setConnectAutoId(null)}
                                    />
                                    <span className="iseg-connect-item__name">None (no automation)</span>
                                </label>
                                {!automationsLoaded ? (
                                    <p className="iseg-detail__empty">Loading automations…</p>
                                ) : automations.length === 0 ? (
                                    <p className="iseg-detail__empty">No automation flows found. Create one in the Automations tab.</p>
                                ) : (
                                    automations.map(auto => (
                                        <label key={auto.id} className="iseg-connect-item">
                                            <input
                                                type="radio"
                                                name="automation"
                                                value={auto.id}
                                                checked={connectAutoId === auto.id}
                                                onChange={() => setConnectAutoId(auto.id)}
                                            />
                                            <span className="iseg-connect-item__name">{auto.name}</span>
                                        </label>
                                    ))
                                )}
                            </div>
                        </div>
                        <div className="crm-modal__footer">
                            <button className="admin-btn" onClick={() => setShowConnectModal(false)} disabled={connectSaving} type="button">Cancel</button>
                            <button
                                className="admin-btn admin-btn--primary"
                                onClick={handleConnectConfirm}
                                disabled={connectSaving}
                                type="button"
                            >
                                {connectSaving ? "Saving…" : "Confirm"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Add Leads Modal ──────────────────────────────────────────── */}
            {showAddLeads && selectedSegment && (
                <div
                    className="crm-modal-overlay"
                    onClick={e => e.target === e.currentTarget && setShowAddLeads(false)}
                >
                    <div className="crm-modal iseg-add-leads-modal">
                        <div className="crm-modal__header">
                            <h2 className="crm-modal__title">Add Leads to "{selectedSegment.name}"</h2>
                            <button className="crm-modal__close" onClick={() => setShowAddLeads(false)} type="button">×</button>
                        </div>

                        {/* Filter bar */}
                        <div className="iseg-add-leads-filters">
                            <input
                                className="admin-search-input"
                                placeholder="Search by first name…"
                                value={alFilters.search}
                                onChange={e => handleAlFilterChange({ search: e.target.value })}
                            />
                            <select
                                className="admin-input"
                                value={alFilters.leadType}
                                onChange={e => handleAlFilterChange({ leadType: e.target.value })}
                            >
                                <option value="">All Types</option>
                                {LEAD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </select>
                            <select
                                className="admin-input"
                                value={alFilters.state}
                                onChange={e => handleAlFilterChange({ state: e.target.value })}
                            >
                                <option value="">All States</option>
                                {US_STATES.map(s => <option key={s.abbr} value={s.abbr}>{s.name}</option>)}
                            </select>
                            <select
                                className="admin-input"
                                value={alFilters.city}
                                onChange={e => handleAlFilterChange({ city: e.target.value })}
                                disabled={!alFilters.state}
                            >
                                <option value="">All Cities</option>
                                {alAvailableCities.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                            <span className="admin-search-count">{alTotal} lead{alTotal !== 1 ? "s" : ""}</span>
                        </div>

                        {/* Table */}
                        <div className="iseg-add-leads-table-wrap">
                            {alLoading ? (
                                <p className="iseg-detail__empty">Loading…</p>
                            ) : alLeads.length === 0 ? (
                                <p className="iseg-detail__empty">No leads found.</p>
                            ) : (
                                <table className="crm-lead-table iseg-add-leads-table">
                                    <thead>
                                        <tr>
                                            <th style={{ width: 36 }}>
                                                <input
                                                    type="checkbox"
                                                    className="crm-checkbox"
                                                    checked={alLeads.length > 0 && alLeads.every(l => alSelected.has(l.id))}
                                                    ref={el => {
                                                        if (el) el.indeterminate = alLeads.some(l => alSelected.has(l.id)) && !alLeads.every(l => alSelected.has(l.id));
                                                    }}
                                                    onChange={handleAlToggleAll}
                                                />
                                            </th>
                                            <th>Name</th>
                                            <th>Type</th>
                                            <th>State</th>
                                            <th>City</th>
                                            <th>Status</th>
                                            <th>Priority</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {alLeads.map(lead => {
                                            const alreadyInSeg = segLeads.some(sl => sl.id === lead.id);
                                            return (
                                                <tr
                                                    key={lead.id}
                                                    className={alreadyInSeg ? "iseg-al-row--already-added" : ""}
                                                    onClick={() => !alreadyInSeg && handleAlToggleRow(lead.id)}
                                                    style={{ cursor: alreadyInSeg ? "default" : "pointer" }}
                                                >
                                                    <td style={{ width: 36 }} onClick={e => e.stopPropagation()}>
                                                        <input
                                                            type="checkbox"
                                                            className="crm-checkbox"
                                                            checked={alSelected.has(lead.id)}
                                                            disabled={alreadyInSeg}
                                                            onChange={() => !alreadyInSeg && handleAlToggleRow(lead.id)}
                                                        />
                                                    </td>
                                                    <td>
                                                        <span className="iseg-al-name">{lead.firstName} {lead.lastName}</span>
                                                        {alreadyInSeg && <span className="iseg-al-already">Already in segment</span>}
                                                        <br />
                                                        <small style={{ color: "var(--color-text-secondary)" }}>{lead.email}</small>
                                                    </td>
                                                    <td>{lead.leadType ? leadTypeLabel(lead.leadType) : <span style={{ color: "var(--color-text-secondary)" }}>—</span>}</td>
                                                    <td>{lead.state || <span style={{ color: "var(--color-text-secondary)" }}>—</span>}</td>
                                                    <td>{lead.city  || <span style={{ color: "var(--color-text-secondary)" }}>—</span>}</td>
                                                    <td><span className={`crm-status-badge crm-status-badge--${lead.status}`}>{lead.status}</span></td>
                                                    <td><span className={`crm-priority-badge crm-priority-badge--${lead.priority}`}>{lead.priority}</span></td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        {/* Pagination */}
                        {Math.ceil(alTotal / ADD_LEADS_LIMIT) > 1 && (
                            <div className="admin-pagination iseg-add-leads-pagination">
                                <button className="admin-page-btn" disabled={alPage === 1} onClick={() => handleAlPageChange(alPage - 1)}>← Prev</button>
                                <span className="admin-page-info">Page {alPage} of {Math.ceil(alTotal / ADD_LEADS_LIMIT)}</span>
                                <button className="admin-page-btn" disabled={alPage === Math.ceil(alTotal / ADD_LEADS_LIMIT)} onClick={() => handleAlPageChange(alPage + 1)}>Next →</button>
                            </div>
                        )}

                        <div className="crm-modal__footer">
                            <span className="iseg-add-leads-count">
                                {alSelected.size > 0 ? `${alSelected.size} lead${alSelected.size !== 1 ? "s" : ""} selected` : "No leads selected"}
                            </span>
                            <button className="admin-btn" onClick={() => setShowAddLeads(false)} disabled={alSaving} type="button">Cancel</button>
                            <button
                                className="admin-btn admin-btn--primary"
                                onClick={handleAddLeadsConfirm}
                                disabled={alSelected.size === 0 || alSaving}
                                type="button"
                            >
                                {alSaving ? "Adding…" : `Add ${alSelected.size > 0 ? alSelected.size : ""} Lead${alSelected.size !== 1 ? "s" : ""} to Segment`}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Create Segment Modal ─────────────────────────────────────── */}
            {showCreate && activeSection && (
                <div
                    className="crm-modal-overlay"
                    onClick={e => e.target === e.currentTarget && setShowCreate(false)}
                >
                    <div className="crm-modal" style={{ maxWidth: 500 }}>
                        <div className="crm-modal__header">
                            <h2 className="crm-modal__title">
                                New Segment —{" "}
                                <span style={{ color: activeSection.accentColor }}>{activeSection.label}</span>
                            </h2>
                            <button className="crm-modal__close" onClick={() => setShowCreate(false)} type="button">×</button>
                        </div>
                        <div className="crm-modal__body">
                            {createError && <div className="lf-error">{createError}</div>}
                            <div className="admin-form-group">
                                <label>Segment Name *</label>
                                <input
                                    className="admin-input"
                                    placeholder="e.g. Growers in California"
                                    value={form.name}
                                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                                    maxLength={200}
                                    autoFocus
                                />
                            </div>
                            <div className="admin-form-group" style={{ marginTop: "var(--space-3)" }}>
                                <label>Description</label>
                                <textarea
                                    className="admin-input"
                                    rows={2}
                                    placeholder="Optional — describe who this segment targets"
                                    value={form.description}
                                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                                    maxLength={500}
                                    style={{ resize: "vertical" }}
                                />
                            </div>
                            <p className="iseg-modal__filter-hint">
                                All filter fields are optional. Status and Priority default to{" "}
                                <strong>{activeSection.label}</strong> criteria but can be overridden.
                            </p>
                            <div className="admin-form-row">
                                <div className="admin-form-group">
                                    <label>Status</label>
                                    <select
                                        className="admin-input"
                                        value={form.status}
                                        onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                                    >
                                        {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                                    </select>
                                </div>
                                <div className="admin-form-group">
                                    <label>Priority</label>
                                    <select
                                        className="admin-input"
                                        value={form.priority}
                                        onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
                                    >
                                        {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="admin-form-row">
                                <div className="admin-form-group">
                                    <label>State</label>
                                    <select
                                        className="admin-input"
                                        value={form.state}
                                        onChange={e => setForm(f => ({ ...f, state: e.target.value, city: "" }))}
                                    >
                                        <option value="">Any state</option>
                                        {US_STATES.map(s => <option key={s.abbr} value={s.abbr}>{s.name}</option>)}
                                    </select>
                                </div>
                                <div className="admin-form-group">
                                    <label>City</label>
                                    <select
                                        className="admin-input"
                                        value={form.city}
                                        onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
                                        disabled={!form.state}
                                    >
                                        <option value="">Any city</option>
                                        {formCities.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="admin-form-group" style={{ marginTop: "var(--space-3)" }}>
                                <label>Lead Type</label>
                                <select
                                    className="admin-input"
                                    value={form.leadType}
                                    onChange={e => setForm(f => ({ ...f, leadType: e.target.value }))}
                                >
                                    <option value="">Any type</option>
                                    {LEAD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="crm-modal__footer">
                            <button className="admin-btn" onClick={() => setShowCreate(false)} disabled={creating} type="button">Cancel</button>
                            <button className="admin-btn admin-btn--primary" onClick={handleCreate} disabled={creating} type="button">
                                {creating ? "Creating…" : "Create Segment"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InboundSegments;
